// @ts-check
/// <reference types="google.maps" />
export function canvasCreator(google) {
  return class CanvasMarkerOverlay extends google.maps.OverlayView {
    /**
     * @param map {google.maps.Map}
     * @param markers Array<{latitude: number, longitude: number, cluster_size: number}>
     */
    constructor(map, markers) {
      super();
      this.googleMap = map;
      this.markers = markers;
      this.canvas = null;
      this.ctx = null;
      this._prevBounds = null;
      this._visibleClusters = null;
    }

    onAdd() {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.left = '0';
      canvas.style.top = '0';
      canvas.style.pointerEvents = 'none'; // Make it non-interactive
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');

      // Add the canvas to the overlay container
      const panes = this.getPanes();
      panes.overlayLayer.appendChild(canvas);
    }

    draw() {
      if (!this.canvas || !this.ctx) return;

      // Get the map dimensions and resize canvas to match
      const projection = this.getProjection();
      const bounds = this.googleMap.getBounds();
      const ne = projection.fromLatLngToDivPixel(bounds.getNorthEast());
      const sw = projection.fromLatLngToDivPixel(bounds.getSouthWest());

      // Resize canvas to cover the visible map area
      this.canvas.width = Math.abs(ne.x - sw.x);
      this.canvas.height = Math.abs(sw.y - ne.y);
      this.canvas.style.width = this.canvas.width + 'px';
      this.canvas.style.height = this.canvas.height + 'px';
      this.canvas.style.left = sw.x + 'px';
      this.canvas.style.top = ne.y + 'px';

      // Clear the canvas
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      if (
        !this._prevBounds ||
        !this._prevBounds.contains(bounds.getNorthEast()) ||
        !this._prevBounds.contains(bounds.getSouthWest())
      ) {
        // Bounds changed or not set, recalculate
        this._visibleClusters = this.markers.filter((cluster) => {
          const coordinates = new google.maps.LatLng(cluster.latitude, cluster.longitude);
          return bounds.contains(coordinates);
        });

        this._prevBounds = bounds;
      }

      // Draw each cluster
      this._visibleClusters.forEach((cluster) => {
        const coordinates = new google.maps.LatLng(cluster.latitude, cluster.longitude);
        const visible = bounds.contains(coordinates);
        if (!visible) return;

        const point = projection.fromLatLngToDivPixel(coordinates);

        if (!point) return;

        // Calculate position relative to canvas
        const x = point.x - sw.x;
        const y = point.y - ne.y;

        // Calculate radius - using square root for non-linear scaling
        const baseRadius = 28;
        const radius = this.googleMap.getZoom() <= 7 ? baseRadius : baseRadius + Math.cbrt(cluster.cluster_size);

        // Draw circle
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, 2 * Math.PI);
        this.ctx.fillStyle = 'rgba(0, 200, 0, 0.6)';
        this.ctx.fill();
        this.ctx.strokeStyle = 'rgba(0, 150, 0, 0.8)';
        this.ctx.lineWidth = 2;
        this.ctx.stroke();

        // Draw text
        if (radius > 10) {
          // Only draw text if circle is large enough
          this.ctx.font = `${Math.min(radius / 2, 14)}px Arial`;
          this.ctx.fillStyle = 'white';
          this.ctx.textAlign = 'center';
          this.ctx.textBaseline = 'middle';
          this.ctx.fillText(cluster.cluster_size.toString(), x, y);
        }
      });
    }

    onRemove() {
      if (this.canvas) {
        this.canvas.parentNode.removeChild(this.canvas);
        this.canvas = null;
        this.ctx = null;
      }
    }
  };
}
